/**
 * AI model configuration, shared between the client (src/services/aiModels.ts
 * re-exports this) and the `ask` edge function. Previously hand-copied between
 * the two, with the edge function's copy carrying a "(copied from aiModels.ts)"
 * comment acknowledging it — this is the single source of truth (sc-276 D7).
 */

export interface ModelConfig {
  maxTokens: number;
  useMaxTokens: boolean;
  useMaxCompletionTokens: boolean;
  temperature: number;
  timeout: number;
  description: string;
  supportsTemperature: boolean;
}

export const AI_MODEL_CONFIG: Record<string, ModelConfig> = {
  'gpt-4-turbo-preview': {
    maxTokens: 4000,
    useMaxTokens: true,
    useMaxCompletionTokens: false,
    temperature: 0.7,
    timeout: 60000,
    description: 'GPT-4 Turbo Preview - Fast, reliable, uses max_tokens',
    supportsTemperature: true
  },
  'gpt-4': {
    maxTokens: 4000,
    useMaxTokens: true,
    useMaxCompletionTokens: false,
    temperature: 0.7,
    timeout: 60000,
    description: 'GPT-4 - Standard model, uses max_tokens',
    supportsTemperature: true
  },
  'gpt-4o': {
    maxTokens: 4000,
    useMaxTokens: true,
    useMaxCompletionTokens: false,
    temperature: 0.7,
    timeout: 60000,
    description: 'GPT-4o - Omni model, uses max_tokens',
    supportsTemperature: true
  },
  'gpt-4o-mini': {
    maxTokens: 4000,
    useMaxTokens: true,
    useMaxCompletionTokens: false,
    temperature: 0.7,
    timeout: 60000,
    description: 'GPT-4o Mini - Fast mini model, uses max_tokens',
    supportsTemperature: true
  },
  'gpt-5-nano': {
    maxTokens: 40000,
    useMaxTokens: false,
    useMaxCompletionTokens: true,
    temperature: 1.0,
    timeout: 120000,
    description: 'GPT-5 Nano - Latest model, uses max_completion_tokens, higher limits',
    supportsTemperature: false
  },
  'gpt-5-mini': {
    maxTokens: 40000,
    useMaxTokens: false,
    useMaxCompletionTokens: true,
    temperature: 1.0,
    timeout: 120000,
    description: 'GPT-5 Mini - Latest model, uses max_completion_tokens, higher limits',
    supportsTemperature: false
  },
  'gpt-5': {
    maxTokens: 40000,
    useMaxTokens: false,
    useMaxCompletionTokens: true,
    temperature: 1.0,
    timeout: 120000,
    description: 'GPT-5 - Latest full model, uses max_completion_tokens, higher limits',
    supportsTemperature: false
  },
  'gpt-3.5-turbo': {
    maxTokens: 4000,
    useMaxTokens: true,
    useMaxCompletionTokens: false,
    temperature: 0.7,
    timeout: 60000,
    description: 'GPT-3.5 Turbo - Fast, cost-effective, uses max_tokens',
    supportsTemperature: true
  }
};

export const getModelConfig = (modelName: string): ModelConfig => {
  return AI_MODEL_CONFIG[modelName] || AI_MODEL_CONFIG['gpt-4-turbo-preview'];
};

export const getAvailableModels = (): string[] => {
  return Object.keys(AI_MODEL_CONFIG);
};

export const getModelDescription = (modelName: string): string => {
  return AI_MODEL_CONFIG[modelName]?.description || 'Unknown model';
};
